import React, { useEffect, useRef, useState, useCallback, useContext } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, CircularProgress, Slider } from '@material-ui/core';
import { AuthContext } from '../../App';
import { makeStyles } from '@material-ui/core/styles';
import { GoogleMap, useJsApiLoader, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import SpeedIcon from '@material-ui/icons/Speed';
import RoomIcon from '@material-ui/icons/Room';
import PersonIcon from '@material-ui/icons/Person';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#e8edf3',
    overflow: 'hidden',
    borderRadius: 12,
  },
  emptyState: {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 10, color: '#94a3b8',
    background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
    zIndex: 10,
    pointerEvents: 'none',
  },
  emptyIcon: {
    width: 72, height: 72, borderRadius: 16,
    background: 'rgba(99,102,241,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  mapSection: {
    flex: 1,
    position: 'relative',
    minHeight: 0,
  },
  svSection: {
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
    borderTop: '1px solid rgba(0,0,0,0.08)',
    background: '#1e293b',
  },
  infoBar: {
    position: 'absolute',
    top: 8, left: 8, right: 8,
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '5px 10px',
    background: 'rgba(15,23,42,0.65)',
    backdropFilter: 'blur(14px) saturate(160%)',
    WebkitBackdropFilter: 'blur(14px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 8,
    boxShadow: '0 6px 18px -8px rgba(0,0,0,0.35)',
    zIndex: 5,
    color: '#fff',
    pointerEvents: 'none',
  },
  plate: {
    fontSize: '0.74rem', fontWeight: 800,
    color: '#fff', letterSpacing: '0.04em',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  metaChip: {
    height: 18, fontSize: '0.56rem', fontWeight: 700,
    background: 'rgba(255,255,255,0.16)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  mapControls: {
    position: 'absolute',
    bottom: 12, right: 12,
    display: 'flex', flexDirection: 'column', gap: 6,
    zIndex: 5,
  },
  ctrlBtn: {
    width: 36, height: 36,
    background: 'rgba(255,255,255,0.96)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    border: '1px solid rgba(0,0,0,0.06)',
    '&:hover': { background: '#fff' },
  },
  svLoading: {
    position: 'absolute', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#94a3b8', fontSize: '0.75rem',
  },
  svLabel: {
    position: 'absolute', top: 8, left: 10,
    fontSize: '0.58rem', fontWeight: 700,
    color: '#fff', textTransform: 'uppercase', letterSpacing: '0.08em',
    padding: '3px 8px',
    background: 'rgba(0,0,0,0.4)',
    borderRadius: 6,
    backdropFilter: 'blur(6px)',
    zIndex: 3,
  },
  noPosition: {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 6, color: '#94a3b8',
    background: 'rgba(15,23,42,0.9)',
    zIndex: 3,
  },
}));

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
  mapTypeId: 'roadmap',
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

function buildTruckSvg(status, availability) {
  // status: 'moving' | 'idle' | 'stopped' | 'unknown'
  // availability: 'available' | 'unavailable' | 'unknown' — controls the OUTER RING
  const statusPal = {
    moving:  { bg: '#22c55e' },
    idle:    { bg: '#eab308' },
    stopped: { bg: '#ef4444' },
    unknown: { bg: '#94a3b8' },
  };
  const availPal = {
    available:   { ring: '#22c55e', ringSolid: 'rgba(34,197,94,0.55)' },
    unavailable: { ring: '#64748b', ringSolid: 'rgba(100,116,139,0.6)' },
    unknown:     { ring: '#94a3b8', ringSolid: 'rgba(148,163,184,0.35)' },
  };
  const s = statusPal[status] || statusPal.unknown;
  const a = availPal[availability] || availPal.unknown;
  const path = 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5-8.5l1.96 2.5H17V9.5h2.5zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z';
  // Outer availability ring + inner status circle
  return `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
    <circle cx="23" cy="23" r="21" fill="none" stroke="${a.ring}" stroke-width="3" stroke-dasharray="${availability === 'unavailable' ? '4 3' : ''}"/>
    <circle cx="23" cy="23" r="17" fill="${a.ringSolid}"/>
    <circle cx="23" cy="23" r="13" fill="${s.bg}" stroke="#fff" stroke-width="2.5"/>
    <g transform="translate(11 11) scale(0.95)">
      <path d="${path}" fill="#fff"/>
    </g>
  </svg>`;
}

function VehicleLocationPreview({ vehicle, allVehicles, pickupPoint, radiusKm: initialRadiusKm = 10, onRadiusChange, onSelectVehicle }) {
  const classes = useStyles();
  const { clientSettings } = useContext(AuthContext);
  const showTacho = clientSettings?.useTachograph !== false;
  const mapRef = useRef(null);
  const svRef = useRef(null);
  const svPanoramaRef = useRef(null);
  // Mapa satélite usado como fallback quando Street View não cobre o local
  // (Angola sobretudo). Renderiza híbrido zoom 19 + marker no veículo.
  const satRef = useRef(null);
  const satMapRef = useRef(null);
  const [svVisible, setSvVisible] = useState(false);
  const [svAvailable, setSvAvailable] = useState(null); // null = unknown, true/false
  const [svLoading, setSvLoading] = useState(false);
  // Radius: controlled by parent when onRadiusChange is provided, otherwise local state.
  const [localRadiusKm, setLocalRadiusKm] = useState(initialRadiusKm);
  const radiusKm = onRadiusChange ? initialRadiusKm : localRadiusKm;
  const setRadiusKm = (val) => {
    if (onRadiusChange) onRadiusChange(val);
    else setLocalRadiusKm(val);
  };
  // InfoWindow on marker click — shows full vehicle info without selecting it in the list
  const [infoVehicle, setInfoVehicle] = useState(null);
  // When a vehicle is selected via the left panel (list click), also open the InfoWindow
  useEffect(() => {
    if (vehicle && vehicle.id) {
      setInfoVehicle(vehicle);
    } else {
      setInfoVehicle(null);
    }
  }, [vehicle?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!onRadiusChange) setLocalRadiusKm(initialRadiusKm); }, [initialRadiusKm, onRadiusChange]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  const position = vehicle?.position;
  const lat = position?.latitude;
  const lng = position?.longitude;
  const hasPosition = typeof lat === 'number' && typeof lng === 'number';

  const status = vehicle ? (
    vehicle.speed > 3 ? 'moving' :
    vehicle.ignition ? 'idle' :
    hasPosition ? 'stopped' : 'unknown'
  ) : 'unknown';

  // Overview mode — active when we have a pickup point and a list of vehicles
  const hasOverview = !!(pickupPoint && typeof pickupPoint.lat === 'number' && typeof pickupPoint.lng === 'number' && Array.isArray(allVehicles) && allVehicles.length > 0);
  const radiusMeters = radiusKm * 1000;

  // Haversine distance from pickup → vehicle (km)
  function distKm(v) {
    if (!pickupPoint || !v.position) return Infinity;
    const R = 6371;
    const dLat = (v.position.latitude - pickupPoint.lat) * Math.PI / 180;
    const dLng = (v.position.longitude - pickupPoint.lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(pickupPoint.lat * Math.PI / 180) * Math.cos(v.position.latitude * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  // Track when the user last touched the map so we don't fight them.
  const lastInteractionRef = useRef(0);
  const lastAutoCenterRef = useRef(0);
  // Once the operator has interacted with the map (zoom / pan), automatic
  // re-fits stop. The initial frame still happens (so the operator sees
  // the right area on dialog open), but any subsequent retries / observer
  // bursts skip the re-center to avoid hijacking the manual zoom.
  const initialFitDoneRef = useRef(false);

  // Imperative recenter used by the initial fit, the periodic tick, and manual actions.
  // Declared BEFORE onLoadMap so the callback can reference it without TDZ.
  // `force=true` is used by the manual "recenter" button to override the
  // user-interacted guard.
  const doAutoCenter = useCallback((opts = {}) => {
    if (!mapRef.current) return;
    const force = opts.force === true;
    // Skip auto-recenter once the initial fit is done. The retries fired
    // by ResizeObserver / onTilesLoaded / the 100/300/700/1500ms cascade
    // are there to UNBLOCK the first paint, not to keep yanking the map
    // back every time. After the first successful fit, only the manual
    // recenter button (force=true) re-centres.
    if (!force && initialFitDoneRef.current) return;
    if (hasPosition) {
      mapRef.current.setCenter({ lat, lng });
      mapRef.current.setZoom(16);
      lastAutoCenterRef.current = Date.now();
      initialFitDoneRef.current = true;
      return;
    }
    if (hasOverview) {
      const bounds = new window.google.maps.LatLngBounds();
      const latDelta = radiusKm / 111;
      const lngDelta = radiusKm / (111 * Math.cos(pickupPoint.lat * Math.PI / 180));
      bounds.extend({ lat: pickupPoint.lat + latDelta, lng: pickupPoint.lng + lngDelta });
      bounds.extend({ lat: pickupPoint.lat - latDelta, lng: pickupPoint.lng - lngDelta });
      allVehicles.forEach(v => {
        if (v.position && distKm(v) <= radiusKm) {
          bounds.extend({ lat: v.position.latitude, lng: v.position.longitude });
        }
      });
      mapRef.current.fitBounds(bounds, { top: 70, bottom: 20, left: 20, right: 20 });
      lastAutoCenterRef.current = Date.now();
      initialFitDoneRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPosition, lat, lng, hasOverview, pickupPoint?.lat, pickupPoint?.lng, radiusKm, allVehicles]);

  // Keep the latest `doAutoCenter` accessible without re-creating onLoadMap.
  const doAutoCenterRef = useRef(null);
  useEffect(() => { doAutoCenterRef.current = doAutoCenter; }, [doAutoCenter]);
  // Holds the ResizeObserver attached to the map container — disconnected
  // on unmount so it doesn't leak listeners between dialog opens.
  const mapResizeObserverRef = useRef(null);
  useEffect(() => {
    return () => {
      try { mapResizeObserverRef.current?.disconnect(); } catch {}
      mapResizeObserverRef.current = null;
    };
  }, []);

  const onLoadMap = useCallback((map) => {
    mapRef.current = map;
    // Belt-and-braces strategy for "map renders blank until I click on it":
    //
    // 1) ResizeObserver — fires the moment the container gets real dims.
    //    Catches the dialog open animation case.
    // 2) Scheduled retries at 100/300/700/1500 ms — covers cases where
    //    the container had non-zero dims when the map mounted but the
    //    Google internals still skipped the first paint (race between
    //    React commit and Maps idle event).
    // 3) `onTilesLoaded` (above) — final safety net.
    try {
      const container = map.getDiv();
      if (window.ResizeObserver && container) {
        let lastApplied = 0;
        const ro = new ResizeObserver(() => {
          const w = container.offsetWidth;
          const h = container.offsetHeight;
          if (w < 50 || h < 50) return;
          const now = Date.now();
          if (now - lastApplied < 80) return;
          lastApplied = now;
          if (window.google?.maps?.event) {
            try { window.google.maps.event.trigger(map, 'resize'); } catch {}
          }
          if (doAutoCenterRef.current) doAutoCenterRef.current();
        });
        ro.observe(container);
        mapResizeObserverRef.current = ro;
      }
    } catch (err) {
      console.warn('[map] ResizeObserver setup failed:', err.message);
    }
    // Scheduled resize+recenter passes — at least one of these is always
    // late enough that the dialog animation has settled and Maps re-paints.
    [100, 300, 700, 1500].forEach(delay => {
      setTimeout(() => {
        if (!mapRef.current) return;
        try {
          if (window.google?.maps?.event) {
            window.google.maps.event.trigger(mapRef.current, 'resize');
          }
          if (doAutoCenterRef.current) doAutoCenterRef.current();
        } catch { /* ignore */ }
      }, delay);
    });
  }, []);

  // Auto-recenter on pickup / vehicle changes (operator picked a favourite,
  // or selected a vehicle from the list). The operator EXPECTS the map to
  // jump to the new selection, so we treat this as an explicit action and
  // bypass the initial-fit-done guard via `force: true`.
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const t = setTimeout(() => {
      const map = mapRef.current;
      if (!map) return;
      try {
        if (window.google?.maps?.event) {
          window.google.maps.event.trigger(map, 'resize');
        }
      } catch { /* ignore */ }
      doAutoCenter({ force: true });
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, pickupPoint?.lat, pickupPoint?.lng, hasPosition, lat, lng]);

  // Hook Google Maps events so we know when the user is actively panning/zooming.
  // While the user is interacting, auto-recenter is suppressed.
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const map = mapRef.current;
    const mark = () => { lastInteractionRef.current = Date.now(); };
    const listeners = [
      map.addListener('dragstart', mark),
      map.addListener('drag', mark),
      map.addListener('zoom_changed', mark),
      map.addListener('mousedown', mark),
      map.addListener('wheel', mark),
    ];
    return () => listeners.forEach(l => l.remove());
  }, [isLoaded]);

  // Periodic auto-recenter removed — it was interrupting the operator while selecting
  // vehicles. The initial fit (useEffect above) still runs when entering the screen or
  // switching OT, and the manual recenter button in the toolbar is always available.

  // Street View setup
  useEffect(() => {
    if (!isLoaded || !svRef.current || !hasPosition || !svVisible) return;

    setSvLoading(true);
    const svService = new window.google.maps.StreetViewService();
    const applyPano = (data) => {
      if (!svRef.current) return;
      setSvAvailable(true);
      if (svPanoramaRef.current) {
        svPanoramaRef.current.setPosition(data.location.latLng);
      } else {
        svPanoramaRef.current = new window.google.maps.StreetViewPanorama(svRef.current, {
          position: data.location.latLng,
          pov: { heading: 0, pitch: 0 },
          zoom: 1,
          disableDefaultUI: true,
          addressControl: false,
          linksControl: true,
          panControl: true,
          enableCloseButton: false,
          fullscreenControl: false,
          motionTracking: false,
          motionTrackingControl: false,
          showRoadLabels: false,
        });
      }
    };
    // 1.º cobertura oficial outdoor; 2.º fallback sem restrição de fonte e raio
    // maior para apanhar photo spheres de utilizadores (ex.: Angola).
    svService.getPanorama(
      { location: { lat, lng }, radius: 80, source: 'outdoor' },
      (data, status) => {
        if (status === 'OK' && data?.location?.latLng) { setSvLoading(false); applyPano(data); return; }
        svService.getPanorama(
          { location: { lat, lng }, radius: 1000 },
          (data2, status2) => {
            setSvLoading(false);
            if (status2 === 'OK' && data2?.location?.latLng) applyPano(data2);
            else setSvAvailable(false);
          }
        );
      }
    );
  }, [isLoaded, hasPosition, lat, lng, svVisible]);

  // Fallback satélite — quando o Street View não tem cobertura, criamos
  // um Map híbrido (satélite + estradas) zoom 19 no satRef. Mantém o
  // operador informado visualmente (típico em Angola).
  useEffect(() => {
    if (svAvailable !== false || !satRef.current || !isLoaded || !hasPosition) return;
    if (satMapRef.current) {
      satMapRef.current.setCenter({ lat, lng });
      return;
    }
    satMapRef.current = new window.google.maps.Map(satRef.current, {
      center: { lat, lng },
      zoom: 19,
      mapTypeId: 'hybrid',
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: false,
      gestureHandling: 'greedy',
    });
    new window.google.maps.Marker({ position: { lat, lng }, map: satMapRef.current });
  }, [svAvailable, isLoaded, hasPosition, lat, lng]);

  // Reset do mapa satélite quando o Street View volta a estar disponível
  // (e.g. operador move o foco para um veículo em PT com cobertura).
  useEffect(() => {
    if (svAvailable !== false && satMapRef.current) {
      satMapRef.current = null;
    }
  }, [svAvailable]);

  const handleRecenter = useCallback(() => {
    if (!mapRef.current) return;
    // If a vehicle is focused, center on it
    if (hasPosition) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(16);
      return;
    }
    // Otherwise refit the overview circle + in-range vehicles
    if (hasOverview) {
      const bounds = new window.google.maps.LatLngBounds();
      const latDelta = radiusKm / 111;
      const lngDelta = radiusKm / (111 * Math.cos(pickupPoint.lat * Math.PI / 180));
      bounds.extend({ lat: pickupPoint.lat + latDelta, lng: pickupPoint.lng + lngDelta });
      bounds.extend({ lat: pickupPoint.lat - latDelta, lng: pickupPoint.lng - lngDelta });
      allVehicles.forEach(v => {
        if (v.position && distKm(v) <= radiusKm) {
          bounds.extend({ lat: v.position.latitude, lng: v.position.longitude });
        }
      });
      mapRef.current.fitBounds(bounds, { top: 70, bottom: 20, left: 20, right: 20 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPosition, lat, lng, hasOverview, pickupPoint?.lat, pickupPoint?.lng, radiusKm, allVehicles]);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) mapRef.current.setZoom((mapRef.current.getZoom() || 15) + 1);
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) mapRef.current.setZoom((mapRef.current.getZoom() || 15) - 1);
  }, []);

  if (!isLoaded) {
    return (
      <Box className={classes.root}>
        <Box className={classes.emptyState}>
          <CircularProgress size={28} style={{ color: '#6366f1' }} />
          <Typography style={{ fontSize: '0.78rem' }}>A carregar mapa…</Typography>
        </Box>
      </Box>
    );
  }

  // Compute pre-sorted in/out-of-range lists for overview mode
  const inRange = [];
  const outRange = [];
  if (hasOverview) {
    allVehicles.forEach(v => {
      if (!v.position) return;
      const d = distKm(v);
      if (d <= radiusKm) inRange.push({ v, d });
      else outRange.push({ v, d });
    });
    inRange.sort((a, b) => a.d - b.d);
  }

  // Vehicle marker factory — icon reflects availability (outer ring) and status (inner circle).
  function vehicleIcon(v, isSelected, inside) {
    const st = v.speed > 3 ? 'moving' : v.ignition ? 'idle' : 'stopped';
    // Derive availability from backend `availableForOT` flag. `null` means
    // the backend didn't compute it (no orderId passed) → treat as unknown.
    const availability = v.availableForOT === true ? 'available'
      : v.availableForOT === false ? 'unavailable'
      : 'unknown';
    const size = isSelected ? 48 : 32;
    const opacity = inside ? 1 : 0.5;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildTruckSvg(st, availability))}`,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size / 2),
      // Place the plate label just below the icon so it doesn't overlap the truck SVG.
      labelOrigin: new window.google.maps.Point(size / 2, size + 8),
      opacity,
    };
  }

  return (
    <Box className={classes.root}>
      {/* Map section */}
      <Box className={classes.mapSection} style={{ flex: svVisible && hasPosition ? '1 1 58%' : '1 1 100%' }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          // Use defaultCenter/defaultZoom (uncontrolled): the map respects
          // these on first load, and afterwards the user is free to pan/zoom
          // without us fighting them. Re-centering happens only via
          // doAutoCenter() (initial fit / manual recenter button).
          defaultCenter={hasPosition ? { lat, lng } : (hasOverview ? { lat: pickupPoint.lat, lng: pickupPoint.lng } : { lat: 39.5, lng: -8.0 })}
          defaultZoom={hasPosition ? 16 : (hasOverview ? 10 : 6)}
          options={MAP_OPTIONS}
          onLoad={onLoadMap}
          // Some browsers / animation timings race past the ResizeObserver:
          // by the time the dialog finishes its open animation, the map has
          // already drawn (or stalled) on a 0-px container. `tilesloaded`
          // fires when Maps finishes painting tiles — at that point the
          // container is definitely real, so a manual resize+recenter
          // unblocks the "click anywhere to make the map appear" bug.
          onTilesLoaded={() => {
            if (!mapRef.current) return;
            try {
              if (window.google?.maps?.event) {
                window.google.maps.event.trigger(mapRef.current, 'resize');
              }
              if (doAutoCenterRef.current) doAutoCenterRef.current();
            } catch { /* ignore */ }
          }}
        >
          {/* ─── Overview mode: pickup circle + all vehicles ─── */}
          {hasOverview && (
            <>
              {/* Blue radius circle around pickup */}
              <Circle
                center={{ lat: pickupPoint.lat, lng: pickupPoint.lng }}
                radius={radiusMeters}
                options={{
                  fillColor: '#3b82f6',
                  fillOpacity: 0.08,
                  strokeColor: '#3b82f6',
                  strokeOpacity: 0.55,
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
              {/* Pickup point marker — distinct green flag */}
              <Marker
                position={{ lat: pickupPoint.lat, lng: pickupPoint.lng }}
                icon={{
                  path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                  fillColor: '#22c55e',
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 2,
                  scale: 2,
                  anchor: new window.google.maps.Point(12, 22),
                }}
                title={pickupPoint.name || 'Ponto de recolha'}
                zIndex={9999}
              />
              {/* All vehicles — dimmed if outside radius, full if inside.
                  The plate is shown as a floating label below the marker for quick scanning. */}
              {allVehicles.filter(v => v.position).map(v => {
                const d = distKm(v);
                const inside = d <= radiusKm;
                const isSelected = vehicle && String(vehicle.id) === String(v.id);
                const availLabel = v.availableForOT === true ? 'disponível' : v.availableForOT === false ? 'não disponível' : 'sem verificação';
                // Driver name in the marker hover tooltip — operator usually
                // wants to see who's behind the wheel before clicking, since
                // a "good" vehicle with the wrong driver still rules itself
                // out (skills, hours of service, etc.).
                const drvName = v.driver?.name || v.tacho?.driverName || '';
                return (
                  <Marker
                    key={`ov-${v.id}`}
                    position={{ lat: v.position.latitude, lng: v.position.longitude }}
                    icon={vehicleIcon(v, isSelected, inside)}
                    title={`${v.plate}${drvName ? ` — ${drvName}` : ''} — ${d.toFixed(1)} km · ${availLabel}`}
                    label={{
                      text: v.plate || ' ',
                      color: isSelected ? '#fff' : inside ? '#0f172a' : '#64748b',
                      fontSize: '10px',
                      fontWeight: '700',
                      className: isSelected ? 'vmp-label vmp-label-selected' : inside ? 'vmp-label vmp-label-inside' : 'vmp-label vmp-label-outside',
                    }}
                    zIndex={isSelected ? 500 : inside ? 100 : 50}
                    onClick={() => {
                      setInfoVehicle(v);
                      if (onSelectVehicle) onSelectVehicle(v.id);
                    }}
                  />
                );
              })}
              {/* Info window — full vehicle details on marker click */}
              {infoVehicle && infoVehicle.position && (
                <InfoWindow
                  position={{ lat: infoVehicle.position.latitude, lng: infoVehicle.position.longitude }}
                  onCloseClick={() => setInfoVehicle(null)}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -25),
                    // Prevent Google Maps from panning the map to fit the InfoWindow —
                    // the operator was losing their viewport every time they clicked a vehicle.
                    disableAutoPan: true,
                  }}
                >
                  <Box style={{ minWidth: 260, maxWidth: 320, fontFamily: 'Roboto, sans-serif', padding: '2px 2px 6px' }}>
                    {/* Header */}
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: infoVehicle.speed > 3 ? '#22c55e' : infoVehicle.ignition ? '#eab308' : '#ef4444' }} />
                      <strong style={{ fontSize: 14, color: '#0f172a', letterSpacing: '0.02em' }}>{infoVehicle.plate}</strong>
                      <Box style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                        {infoVehicle.availableForOT === true && (
                          <Chip size="small" label="Disponível" style={{ height: 18, fontSize: '0.55rem', fontWeight: 800, background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }} />
                        )}
                        {infoVehicle.availableForOT === false && (
                          <Chip size="small" label="Não disponível" style={{ height: 18, fontSize: '0.55rem', fontWeight: 800, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }} />
                        )}
                      </Box>
                    </Box>
                    {/* Driver */}
                    {(infoVehicle.driver?.name || infoVehicle.tacho?.driverName) && (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#0f172a', marginBottom: 3 }}>
                        <PersonIcon style={{ fontSize: 13, color: '#8b5cf6' }} />
                        <span style={{ fontWeight: 600 }}>{infoVehicle.driver?.name || infoVehicle.tacho?.driverName}</span>
                      </Box>
                    )}
                    {/* Activity / speed */}
                    <Box style={{ display: 'flex', gap: 10, fontSize: 11, color: '#475569', marginBottom: 6 }}>
                      {infoVehicle.tacho?.activity && (
                        <span>
                          <b style={{ color: infoVehicle.tacho.activity === 'Condução' ? '#f59e0b' : infoVehicle.tacho.activity === 'Descanso' ? '#3b82f6' : '#64748b' }}>
                            {infoVehicle.tacho.activity}
                          </b>
                        </span>
                      )}
                      {infoVehicle.position?.speed != null && (
                        <span><SpeedIcon style={{ fontSize: 10, verticalAlign: 'middle' }} /> {Math.round(infoVehicle.speed || 0)} km/h</span>
                      )}
                      {showTacho && infoVehicle.tacho?.cardInserted != null && (
                        <span style={{ color: infoVehicle.tacho.cardInserted ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                          Cartão: {infoVehicle.tacho.cardInserted ? 'inserido' : 'não inserido'}
                        </span>
                      )}
                    </Box>
                    {/* Address */}
                    {infoVehicle.position?.address && (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: '#94a3b8', marginBottom: 6 }}>
                        <RoomIcon style={{ fontSize: 11 }} />
                        <span style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{infoVehicle.position.address}</span>
                      </Box>
                    )}
                    {/* Tacho stats — only when the tenant uses a tachograph */}
                    {showTacho && infoVehicle.tacho && (
                      <Box style={{ marginTop: 6, padding: '6px 8px', background: '#faf5ff', borderRadius: 6, border: '1px solid #ede9fe' }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                          Tacógrafo
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px', fontSize: 10, color: '#475569' }}>
                          {infoVehicle.tacho.driveAccum && <span>Condução diária: <b>{infoVehicle.tacho.driveAccum}</b></span>}
                          {infoVehicle.tacho.weeklyDrive && <span>Condução semanal: <b>{infoVehicle.tacho.weeklyDrive}</b></span>}
                          {infoVehicle.tacho.breakAccum && <span>Pausa acumulada: <b>{infoVehicle.tacho.breakAccum}</b></span>}
                          {infoVehicle.tacho.dailyRest && <span>Descanso diário: <b>{infoVehicle.tacho.dailyRest}</b></span>}
                          {infoVehicle.tacho.nextBreak && <span style={{ color: '#f59e0b' }}>Próx. pausa: <b>{infoVehicle.tacho.nextBreak}</b></span>}
                        </div>
                      </Box>
                    )}
                    {/* Availability reasons */}
                    {infoVehicle.availableForOT === false && Array.isArray(infoVehicle.availabilityReasons) && infoVehicle.availabilityReasons.length > 0 && (
                      <Box style={{ marginTop: 6, padding: '6px 8px', background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca' }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                          Não disponível porque:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 14, fontSize: 10, color: '#991b1b' }}>
                          {infoVehicle.availabilityReasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </Box>
                    )}
                  </Box>
                </InfoWindow>
              )}
            </>
          )}

          {/* ─── Detail mode (single vehicle selected, no overview) ─── */}
          {!hasOverview && hasPosition && (
            <>
              <Circle
                center={{ lat, lng }}
                radius={30}
                options={{
                  fillColor: status === 'moving' ? '#22c55e' : status === 'idle' ? '#eab308' : '#ef4444',
                  fillOpacity: 0.14,
                  strokeColor: status === 'moving' ? '#22c55e' : status === 'idle' ? '#eab308' : '#ef4444',
                  strokeOpacity: 0.45,
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
              <Marker
                position={{ lat, lng }}
                icon={{
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildTruckSvg(status))}`,
                  scaledSize: new window.google.maps.Size(46, 46),
                  anchor: new window.google.maps.Point(23, 23),
                }}
              />
            </>
          )}
        </GoogleMap>

        {/* Info overlay */}
        {vehicle && (
          <Box className={classes.infoBar}>
            <LocalShippingIcon style={{ fontSize: 18, color: '#fff' }} />
            <Typography className={classes.plate}>{vehicle.plate || '—'}</Typography>
            {(vehicle.driver?.name || vehicle.tacho?.driverName) && (
              <Chip size="small" className={classes.metaChip}
                label={<span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><PersonIcon style={{ fontSize: 11 }} />{vehicle.driver?.name || vehicle.tacho?.driverName}</span>} />
            )}
            {hasPosition && (
              <Chip size="small" className={classes.metaChip}
                label={<span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><SpeedIcon style={{ fontSize: 11 }} />{Math.round(vehicle.speed || 0)} km/h</span>} />
            )}
            {vehicle.position?.address && (
              <Chip size="small" className={classes.metaChip}
                label={<span style={{ display: 'flex', alignItems: 'center', gap: 3, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><RoomIcon style={{ fontSize: 11 }} />{vehicle.position.address}</span>} />
            )}
          </Box>
        )}
        {/* Overview info bar — shown when no vehicle is selected yet.
            Compact version: small icon + name + slim radius slider + counts. */}
        {!vehicle && hasOverview && (
          <Box className={classes.infoBar} style={{ pointerEvents: 'auto' }}>
            <RoomIcon style={{ fontSize: 14, color: '#22c55e' }} />
            <Typography className={classes.plate} style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pickupPoint.name || 'Ponto de recolha'}
            </Typography>
            <Box style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '0 8px',
              background: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 999,
              minWidth: 150,
              height: 22,
            }}>
              <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                Raio
              </Typography>
              <Slider
                value={radiusKm}
                min={5}
                max={300}
                step={5}
                onChange={(e, val) => setRadiusKm(val)}
                onChangeCommitted={() => handleRecenter()}
                style={{ width: 80, color: '#fff', padding: '6px 0' }}
                marks={false}
              />
              <Typography style={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', minWidth: 38, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {radiusKm} km
              </Typography>
            </Box>
            <Chip size="small" className={classes.metaChip}
              style={{ background: 'rgba(34,197,94,0.35)', border: '1px solid rgba(134,239,172,0.45)', height: 18 }}
              label={`${inRange.length} dentro`} />
            <Chip size="small" className={classes.metaChip}
              style={{ background: 'rgba(148,163,184,0.3)', border: '1px solid rgba(203,213,225,0.45)', height: 18 }}
              label={`${outRange.length} fora`} />
          </Box>
        )}

        {/* Custom controls */}
        {hasPosition && (
          <Box className={classes.mapControls}>
            <Tooltip title="Centrar no veículo" placement="left">
              <IconButton size="small" className={classes.ctrlBtn} onClick={handleRecenter}>
                <CenterFocusStrongIcon style={{ fontSize: 18, color: '#3b82f6' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={svVisible ? 'Esconder Street View' : 'Mostrar Street View'} placement="left">
              <IconButton size="small" className={classes.ctrlBtn} onClick={() => setSvVisible(v => !v)}>
                {svVisible ? <VisibilityOffIcon style={{ fontSize: 18, color: '#64748b' }} /> : <VisibilityIcon style={{ fontSize: 18, color: '#22c55e' }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Mais zoom" placement="left">
              <IconButton size="small" className={classes.ctrlBtn} onClick={handleZoomIn}>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#334155' }}>+</span>
              </IconButton>
            </Tooltip>
            <Tooltip title="Menos zoom" placement="left">
              <IconButton size="small" className={classes.ctrlBtn} onClick={handleZoomOut}>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#334155' }}>−</span>
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* No position warning */}
        {!hasPosition && vehicle && (
          <Box className={classes.emptyState}>
            <Box className={classes.emptyIcon}>
              <RoomIcon style={{ fontSize: 36, color: '#cbd5e1' }} />
            </Box>
            <Typography style={{ fontSize: '0.82rem', fontWeight: 600 }}>
              Veículo sem posição GPS recente
            </Typography>
            <Typography style={{ fontSize: '0.68rem' }}>
              Não é possível mostrar no mapa
            </Typography>
          </Box>
        )}
      </Box>

      {/* Street View section — always mounted to keep svRef/svPanoramaRef stable; hidden via display */}
      <Box className={classes.svSection}
        style={{
          display: svVisible && hasPosition ? 'block' : 'none',
          flex: '1 1 42%',
          minHeight: 160,
        }}>
        <Typography className={classes.svLabel}>
          {svAvailable === false ? '🛰️ Satélite' : 'Street View'}
        </Typography>
        <Box ref={svRef} style={{
          width: '100%', height: '100%',
          display: svAvailable === false ? 'none' : 'block',
        }} />
        {/* Fallback satélite — substitui a mensagem "não disponível" por
            um mapa híbrido zoom 19 quando o Street View não tem cobertura
            (típico em Angola). Operador continua a ter referência visual. */}
        <Box ref={satRef} style={{
          width: '100%', height: '100%',
          display: svAvailable === false && !svLoading ? 'block' : 'none',
          position: 'absolute', inset: 0,
        }} />
        {svLoading && (
          <Box className={classes.svLoading}>
            <CircularProgress size={20} style={{ color: '#94a3b8' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default React.memo(VehicleLocationPreview);
